import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface ThemeConfig {
  // Colors
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  accent: string;
  accentForeground: string;
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  muted: string;
  mutedForeground: string;
  border: string;
  destructive: string;
  destructiveForeground: string;
  success: string;
  successForeground: string;
  
  // Spacing & Sizing
  radius: number;
  
  // Typography
  fontSize: number;
}

const defaultTheme: ThemeConfig = {
  // Light Blue Theme (HSL values without 'hsl()' wrapper)
  primary: '222.2 47.4% 11.2%',
  primaryForeground: '210 40% 98%',
  secondary: '200 90% 94%',
  secondaryForeground: '222.2 47.4% 11.2%',
  accent: '205 90% 93%',
  accentForeground: '222.2 47.4% 11.2%',
  background: '210 100% 98%',
  foreground: '222.2 84% 4.9%',
  card: '210 100% 99%',
  cardForeground: '222.2 84% 4.9%',
  muted: '200 85% 92%',
  mutedForeground: '215.4 16.3% 46.9%',
  border: '200 80% 88%',
  destructive: '0 84.2% 60.2%',
  destructiveForeground: '210 40% 98%',
  success: '142.1 76.2% 36.3%',
  successForeground: '210 40% 98%',
  
  radius: 8,
  fontSize: 16,
};

interface ThemeContextType {
  theme: ThemeConfig;
  updateTheme: (updates: Partial<ThemeConfig>) => void;
  resetTheme: () => void;
  exportTheme: () => string;
  importTheme: (json: string) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setTheme] = useState<ThemeConfig>(() => {
    const saved = localStorage.getItem('app-theme-config');
    return saved ? JSON.parse(saved) : defaultTheme;
  });

  // Apply theme to CSS variables
  useEffect(() => {
    const root = document.documentElement;
    
    // Apply color variables
    root.style.setProperty('--primary', theme.primary);
    root.style.setProperty('--primary-foreground', theme.primaryForeground);
    root.style.setProperty('--secondary', theme.secondary);
    root.style.setProperty('--secondary-foreground', theme.secondaryForeground);
    root.style.setProperty('--accent', theme.accent);
    root.style.setProperty('--accent-foreground', theme.accentForeground);
    root.style.setProperty('--background', theme.background);
    root.style.setProperty('--foreground', theme.foreground);
    root.style.setProperty('--card', theme.card);
    root.style.setProperty('--card-foreground', theme.cardForeground);
    root.style.setProperty('--muted', theme.muted);
    root.style.setProperty('--muted-foreground', theme.mutedForeground);
    root.style.setProperty('--border', theme.border);
    root.style.setProperty('--destructive', theme.destructive);
    root.style.setProperty('--destructive-foreground', theme.destructiveForeground);
    root.style.setProperty('--success', theme.success);
    root.style.setProperty('--success-foreground', theme.successForeground);
    
    // Apply sizing variables
    root.style.setProperty('--radius', `${theme.radius}px`);
    root.style.fontSize = `${theme.fontSize}px`;
    
    // Persist to localStorage
    localStorage.setItem('app-theme-config', JSON.stringify(theme));
  }, [theme]);

  const updateTheme = (updates: Partial<ThemeConfig>) => {
    setTheme((prev) => ({ ...prev, ...updates }));
  };

  const resetTheme = () => {
    setTheme(defaultTheme);
    localStorage.removeItem('app-theme-config');
  };

  const exportTheme = () => {
    return JSON.stringify(theme, null, 2);
  };

  const importTheme = (json: string) => {
    try {
      const imported = JSON.parse(json);
      setTheme(imported);
    } catch (error) {
      console.error('Invalid theme JSON:', error);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, resetTheme, exportTheme, importTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
